// PaneSizePreference — ClassicLayout の grid 境界 drag-resize ハンドル (= chat
// 行高 / member 列幅) の base px (= chatZoom を掛ける前の raw 値) を
// localStorage に永続化する infrastructure 層ユーティリティ.
//
// (2026-05-17 追補: uiScalePreference.ts と同流儀の storage-injection 抽象)
//
// Design:
// - **対象**: ClassicLayout の grid 親 `gridTemplateRows` 下行 (= chat 行高) と
//   `gridTemplateColumns` 3 列目 (= member 列幅). drag-resize splitter から
//   リアルタイムに書き込む.
// - **値**: raw px (= 個人 UI スケール `chatZoom` を掛ける前の base 値).
//   ClassicLayout 側で `chatRowPx * chatZoom` / `memberColPx * chatZoom` として
//   grid template に流す. scale を掛けた "見た目 px" を保存すると scale 変更時に
//   レイアウトが二重に拡縮されるため、raw px を保存する.
// - **clamp**: chat 行高 80..400 / member 列幅 80..360. drag 中にリアルタイム
//   クランプし、不正値が保存されても read 側で再 clamp して fallback.
// - **不正値 / 不在時の挙動**: 数値文字列で保存. parse 失敗 (`NaN` / 非有限) や
//   範囲外は default にフォールバック. 例外を throw しない.
// - **scope**: ユーザー端末ローカル, サーバ同期なし. protocol 変更なし.
//   独立 2 キー: chat 行高 / member 列幅.
//
// Tests: paneSizePreference.test.ts (storage-injection style).

/** localStorage キー: chat 行高 (raw px, chatZoom を掛ける前). */
export const CHAT_ROW_PX_KEY = 'oekaki:chat-row-px';
/** localStorage キー: member 列幅 (raw px, chatZoom を掛ける前). */
export const MEMBER_COL_PX_KEY = 'oekaki:member-col-px';
/**
 * localStorage キー: Modern Layout 中右ドックの member 欄高さ (px).
 * (2026-06-08 追補: Modern の メンバー⇄チャット間 drag-resize splitter 用.
 * Classic の grid 境界とは別レイアウト・別単位 (縦の固定 px 高さ) なので独立 key.)
 */
export const MODERN_MEMBER_HEIGHT_PX_KEY = 'oekaki:modern-member-h-px';

/** chat 行高デフォルト (= 旧 R14 grid `180px` 踏襲). */
export const DEFAULT_CHAT_ROW_PX = 180;
/** member 列幅デフォルト (= 旧 R14 grid `140px` 踏襲). */
export const DEFAULT_MEMBER_COL_PX = 140;
/**
 * Modern member 欄高さデフォルト (= consult 2026-06-08「もう少し縦に広げたい」
 * を受け、旧 150px 上限から 220px に拡大). リサイザの初期値もこれに合わせる.
 */
export const DEFAULT_MODERN_MEMBER_HEIGHT_PX = 220;

/** chat 行高 clamp 範囲. */
export const CHAT_ROW_PX_MIN = 80;
export const CHAT_ROW_PX_MAX = 400;
/** member 列幅 clamp 範囲. */
export const MEMBER_COL_PX_MIN = 80;
export const MEMBER_COL_PX_MAX = 360;
/**
 * Modern member 欄高さ clamp 範囲. 上限 480 は固定の安全弁 (= ドック全高に対する
 * 動的上限は ModernSocialDock 側で別途 chat 最低高さを残すよう算出する).
 */
export const MODERN_MEMBER_HEIGHT_PX_MIN = 120;
export const MODERN_MEMBER_HEIGHT_PX_MAX = 480;

/**
 * 数値を `[min, max]` に閉じ込める. NaN / Infinity / 非数値は default にフォール
 * バック. drag 中 / read 時両方で同じロジックを使うので 1 箇所に集約.
 */
export const clampChatRowPx = (n: number): number => {
  if (!Number.isFinite(n)) return DEFAULT_CHAT_ROW_PX;
  if (n < CHAT_ROW_PX_MIN) return CHAT_ROW_PX_MIN;
  if (n > CHAT_ROW_PX_MAX) return CHAT_ROW_PX_MAX;
  return n;
};

export const clampMemberColPx = (n: number): number => {
  if (!Number.isFinite(n)) return DEFAULT_MEMBER_COL_PX;
  if (n < MEMBER_COL_PX_MIN) return MEMBER_COL_PX_MIN;
  if (n > MEMBER_COL_PX_MAX) return MEMBER_COL_PX_MAX;
  return n;
};

export const clampModernMemberHeightPx = (n: number): number => {
  if (!Number.isFinite(n)) return DEFAULT_MODERN_MEMBER_HEIGHT_PX;
  if (n < MODERN_MEMBER_HEIGHT_PX_MIN) return MODERN_MEMBER_HEIGHT_PX_MIN;
  if (n > MODERN_MEMBER_HEIGHT_PX_MAX) return MODERN_MEMBER_HEIGHT_PX_MAX;
  return n;
};

/**
 * Minimal Storage shape this module uses (uiScalePreference / AdminTokenStore /
 * AnonUidStore と同流儀). `globalThis.localStorage` matches; tests pass a mock
 * with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface PaneSizePreferenceStore {
  /** chat 行高を読み出し. 不在 / 不正 / storage 不可 → `DEFAULT_CHAT_ROW_PX`. */
  getChatRowPx(): number;
  /** member 列幅を読み出し. 不在 / 不正 / storage 不可 → `DEFAULT_MEMBER_COL_PX`. */
  getMemberColPx(): number;
  /**
   * Modern member 欄高さを読み出し. 不在 / 不正 / storage 不可 →
   * `DEFAULT_MODERN_MEMBER_HEIGHT_PX`.
   */
  getModernMemberHeightPx(): number;
  /** chat 行高を保存 (clamp 適用). storage 不可なら no-op. */
  setChatRowPx(px: number): void;
  /** member 列幅を保存 (clamp 適用). storage 不可なら no-op. */
  setMemberColPx(px: number): void;
  /** Modern member 欄高さを保存 (clamp 適用). storage 不可なら no-op. */
  setModernMemberHeightPx(px: number): void;
  /** 全キーを削除. テスト / 設定リセット用. storage 不可なら no-op. */
  clear(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`.
 * uiScalePreference.ts の probe と同形 (sentinel write + readback).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:pane-size__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreatePaneSizePreferenceStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path.
   */
  storage?: StorageLike | null;
}

/** 単一 key を数値として読み出し → clamp. 失敗時 default. */
const readPx = (
  storage: StorageLike,
  key: string,
  clamp: (n: number) => number,
  fallback: number,
): number => {
  try {
    const raw = storage.getItem(key);
    if (raw === null) return fallback;
    // `Number('')` is `0` (= clamps to 80 instead of falling back). 空文字 /
    // whitespace は明示で default に倒す.
    if (raw.trim() === '') return fallback;
    const n = Number(raw);
    if (!Number.isFinite(n)) return fallback;
    return clamp(n);
  } catch {
    return fallback;
  }
};

/** 単一 key に書き込み (clamp 適用). 失敗時 silently drop. */
const writePx = (
  storage: StorageLike,
  key: string,
  px: number,
  clamp: (n: number) => number,
): void => {
  try {
    storage.setItem(key, String(clamp(px)));
  } catch {
    // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる.
  }
};

/**
 * `PaneSizePreferenceStore` のインスタンスを構築. storage が unavailable な
 * 環境 (SSR / Safari private browsing 厳格モード) でも getters は default を
 * 返すだけで、setters は no-op.
 */
export const createPaneSizePreferenceStore = (
  opts: CreatePaneSizePreferenceStoreOptions = {},
): PaneSizePreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    getChatRowPx() {
      if (storage === null) return DEFAULT_CHAT_ROW_PX;
      return readPx(storage, CHAT_ROW_PX_KEY, clampChatRowPx, DEFAULT_CHAT_ROW_PX);
    },
    getMemberColPx() {
      if (storage === null) return DEFAULT_MEMBER_COL_PX;
      return readPx(storage, MEMBER_COL_PX_KEY, clampMemberColPx, DEFAULT_MEMBER_COL_PX);
    },
    getModernMemberHeightPx() {
      if (storage === null) return DEFAULT_MODERN_MEMBER_HEIGHT_PX;
      return readPx(
        storage,
        MODERN_MEMBER_HEIGHT_PX_KEY,
        clampModernMemberHeightPx,
        DEFAULT_MODERN_MEMBER_HEIGHT_PX,
      );
    },
    setChatRowPx(px) {
      if (storage === null) return;
      writePx(storage, CHAT_ROW_PX_KEY, px, clampChatRowPx);
    },
    setMemberColPx(px) {
      if (storage === null) return;
      writePx(storage, MEMBER_COL_PX_KEY, px, clampMemberColPx);
    },
    setModernMemberHeightPx(px) {
      if (storage === null) return;
      writePx(storage, MODERN_MEMBER_HEIGHT_PX_KEY, px, clampModernMemberHeightPx);
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(CHAT_ROW_PX_KEY);
        storage.removeItem(MEMBER_COL_PX_KEY);
        storage.removeItem(MODERN_MEMBER_HEIGHT_PX_KEY);
      } catch {
        // ignore
      }
    },
  };
};
